// src/components/CompanyArticlesSection.jsx
import React, { useEffect, useState, useMemo, useRef } from "react";
import DOMPurify from "dompurify";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  TextField,
  Button,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Tabs,
  Tab,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from "@mui/material";

import { EditOutlined as EditOutlinedIcon } from "../../icons/FigmaControlIcons";
import { DeleteOutline as DeleteOutlineIcon } from "../../icons/FigmaControlIcons";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Search as SearchIcon } from "../../icons/FigmaControlIcons";
import { TAB_LIGHT_BLUE } from "../CompanyTabHeaderShared";
import TabSectionHeader from "../../TabSectionHeader";
import { formatLocalDateTime, toTimeValue } from "../../../utils/dateTime";

/**
 * ✅ CRA API base
 * Dev:  REACT_APP_API_BASE=http://localhost:5000/api
 * Prod: REACT_APP_API_BASE=/rpo/api
 *
 * Fallback prevents production from ever hitting localhost.
 */
const API_BASE = process.env.REACT_APP_API_BASE;

function formatDateTime(value) {
  return formatLocalDateTime(value, {}, "");
}

function moveItem(arr, fromIndex, toIndex) {
  if (fromIndex === toIndex) return arr;
  const next = [...arr];
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item);
  return next;
}

/** -------------------------
 *  ✅ Helpers for AI JSON
 * ------------------------*/
function coerceArticlesPayload(payload) {
  // Accept:
  //  - Array
  //  - { articles: [...] }
  //  - JSON string
  //  - LLM response containing ```json ... ```
  let data = payload;

  if (typeof data === "string") {
    const raw = data.trim();
    const fenced = raw.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
    const cleaned = (fenced?.[1] || raw).trim();

    try {
      data = JSON.parse(cleaned);
    } catch {
      const arrMatch = cleaned.match(/\[[\s\S]*\]/);
      const objMatch = cleaned.match(/\{[\s\S]*\}/);
      const candidate = (arrMatch?.[0] || objMatch?.[0] || "").trim();
      if (!candidate) throw new Error("AI response is not valid JSON.");
      data = JSON.parse(candidate);
    }
  }

  if (Array.isArray(data)) return data;

  if (data && typeof data === "object") {
    if (Array.isArray(data.articles)) return data.articles;
    if (Array.isArray(data.items)) return data.items;
  }

  throw new Error("AI response JSON must be an array (or { articles: [...] }).");
}

function normalizeArticle(a) {
  const title = String(a?.title || "").trim();
  const content = String(a?.content || "").trim();

  const featured_image = String(
    a?.featured_image || a?.featuredImage || a?.image || ""
  ).trim();

  const author = String(a?.author || a?.byline || "").trim();

  const publish_date = String(
    a?.publish_date || a?.published_at || a?.publishedAt || a?.date || ""
  ).trim();

  return { title, content, featured_image, author, publish_date };
}

export default function CompanyArticlesSection({ companyId }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [actionError, setActionError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // ✅ Sort dropdown
  const [sortMode, setSortMode] = useState("manual");

  // ✅ Search bar (title + content)
  const [searchQuery, setSearchQuery] = useState("");

  // ✅ NEW: prompt-only create
  const [aiPrompt, setAiPrompt] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState({
    title: "",
    featured_image: "",
    author: "",
    categories: "",
    content: "",
    meta_title: "",
    meta_description: "",
    meta_keywords: "",
  });

  const [articleDialogOpen, setArticleDialogOpen] = useState(false);
  const [newPostDialogOpen, setNewPostDialogOpen] = useState(false);

  const [editActiveTab, setEditActiveTab] = useState(0);

  const [uploadingEditImage, setUploadingEditImage] = useState(false);
  const editImageInputRef = useRef(null);

  // --- pagination ---
  const [page, setPage] = useState(1);
  const POSTS_PER_PAGE = 6;

  // --- drag / drop reorder (no library) ---
  const dragFromAbsIndexRef = useRef(null);
  const [draggingPostId, setDraggingPostId] = useState(null);
  const [dragOverPostId, setDragOverPostId] = useState(null);

  // ✅ Robust token getter
  const getToken = () =>
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    "";

  // --- auth + role from localStorage ---
  const { user, canEdit } = useMemo(() => {
    let storedUser = null;
    try {
      storedUser = JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      storedUser = null;
    }

    const role = (storedUser?.role || "").toLowerCase();
    const editable =
      role === "recruiter" || role === "administrator" || role === "admin" || role === "system_user";

    return { user: storedUser, canEdit: editable };
  }, []);

  // ✅ Load posts
  useEffect(() => {
    if (!companyId) return;

    const fetchPosts = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const authToken = getToken();
        const res = await fetch(`${API_BASE}/api/companies/${companyId}/posts`, {
          headers: authToken ? { Authorization: `Bearer ${authToken.replace(/^Bearer\s+/i, "")}` } : {},
          credentials: "include",
        });

        if (!res.ok) {
          const text = await res.text();
          console.error(
            "[CompanyArticlesSection] Non-OK response:",
            res.status,
            text.slice(0, 200)
          );
          throw new Error(
            `Failed to load posts (status ${res.status}). Check API route /api/companies/${companyId}/posts`
          );
        }

        const contentType = res.headers.get("content-type") || "";
        if (!contentType.includes("application/json")) {
          const text = await res.text();
          console.error(
            "[CompanyArticlesSection] Expected JSON, got:",
            contentType,
            "Body preview:",
            text.slice(0, 200)
          );
          throw new Error(
            "Server returned non-JSON response. Check that the Express API is handling /api/companies/:companyId/posts."
          );
        }

        const data = await res.json();
        setPosts(Array.isArray(data) ? data : []);
        setPage(1);
      } catch (err) {
        console.error("Error loading posts:", err);
        setLoadError(err.message || "Failed to load posts");
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [companyId]);

  // ✅ Reset pagination when user changes sort/search
  useEffect(() => {
    setPage(1);
  }, [sortMode, searchQuery]);

  const parseCategories = (raw) => {
    if (!raw) return [];
    return String(raw)
      .split(",")
      .map((c) => c.trim())
      .filter(Boolean);
  };

  // ======================
  //   IMAGE UPLOAD (EDIT ONLY)
  // ======================
  const uploadEditPostImage = async (file) => {
    if (!file) return;

    const authToken = getToken();
    if (!authToken || !canEdit) {
      setActionError(
        "You do not have permission to upload images (Recruiter / Administrator only) or token missing."
      );
      return;
    }

    const sizeMb = file.size / (1024 * 1024);
    if (sizeMb > 5) {
      setActionError("Image is too large. Max size is 5 MB.");
      return;
    }

    const formData = new FormData();
    formData.append("image", file);

    setUploadingEditImage(true);
    setActionError("");
    try {
      const res = await fetch(`${API_BASE}/upload/post-image`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        body: formData,
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.error || "Failed to upload image");
      }

      const data = await res.json();
      if (!data.url) throw new Error("Upload response missing image URL");

      setEditingData((prev) => ({ ...prev, featured_image: data.url }));
    } catch (err) {
      console.error("Image upload error:", err);
      setActionError(err.message || "Failed to upload image");
    } finally {
      setUploadingEditImage(false);
    }
  };

  const handleEditImageFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadEditPostImage(file);
    e.target.value = "";
  };

  // ======================
  //   CREATE (AI PROMPT)
  // ======================
  const handleCreateFromPrompt = async () => {
    setActionError("");

    const prompt = String(aiPrompt || "").trim();
    if (!prompt) {
      setActionError("OpenAI prompt is required.");
      return;
    }

    const authToken = getToken();
    if (!authToken || !canEdit) {
      setActionError(
        "You do not have permission to create posts (Recruiter / Administrator only)."
      );
      return;
    }

    setSubmitting(true);

    try {
      const aiRes = await fetch(
        `${API_BASE}/api/companies/${companyId}/posts/ai`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            prompt,
            schema_hint:
              "Return ONLY valid JSON: an array of objects with keys featured_image, publish_date, title, author, content.",
          }),
        }
      );

      if (!aiRes.ok) {
        const text = await aiRes.text().catch(() => "");
        throw new Error(
          `AI generation failed (status ${aiRes.status}). ${text?.slice(0, 160)}`
        );
      }

      const ct = aiRes.headers.get("content-type") || "";
      let aiPayload;
      if (ct.includes("application/json")) aiPayload = await aiRes.json();
      else aiPayload = await aiRes.text();

      const rawArticles = coerceArticlesPayload(aiPayload)
        .map(normalizeArticle)
        .filter((a) => a.title && a.content);

      if (rawArticles.length === 0) {
        throw new Error(
          "AI returned no valid articles. Ensure each item includes title + content."
        );
      }

      const createdPosts = [];
      for (const a of rawArticles) {
        const res = await fetch(
          `${API_BASE}/api/companies/${companyId}/posts`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify({
              title: a.title,
              featured_image: a.featured_image || null,
              author: a.author || user?.name || null,
              categories: null,
              content: a.content, // ✅ HTML content allowed (we render it safely)
              published_at: a.publish_date || null,
              meta_title: null,
              meta_description: null,
              meta_keywords: null,
            }),
          }
        );

        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          throw new Error(
            errBody.error || "Failed to create a post from AI output"
          );
        }

        const created = await res.json();
        createdPosts.push(created);
      }

      setPosts((prev) => [...createdPosts.reverse(), ...prev]);
      setPage(1);

      setAiPrompt("");
      setNewPostDialogOpen(false);
    } catch (err) {
      console.error("Error creating post(s) from prompt:", err);
      setActionError(err.message || "Failed to create post(s) from prompt");
    } finally {
      setSubmitting(false);
    }
  };

  // ======================
  //   DUPLICATE
  // ======================
  const handleDuplicate = async (post) => {
    const authToken = getToken();
    if (!authToken || !canEdit) {
      setActionError(
        "You do not have permission to duplicate posts (Recruiter / Administrator only)."
      );
      return;
    }

    setSubmitting(true);
    setActionError("");
    try {
      const res = await fetch(`${API_BASE}/api/companies/${companyId}/posts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          title: `Copy of ${post.title || "Untitled"}`,
          featured_image: post.featured_image || null,
          author: post.author || user?.name || null,
          categories: post.categories || null,
          content: post.content || "",
          meta_title: post.meta_title || null,
          meta_description: post.meta_description || null,
          meta_keywords: post.meta_keywords || null,
        }),
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.error || "Failed to duplicate post");
      }

      const duplicated = await res.json();
      setPosts((prev) => [duplicated, ...prev]);
      setPage(1);
    } catch (err) {
      console.error("Error duplicating post:", err);
      setActionError(err.message || "Failed to duplicate post");
    } finally {
      setSubmitting(false);
    }
  };

  // ======================
  //   EDIT / DIALOG
  // ======================
  const startEdit = (post) => {
    if (!canEdit) return;
    if (draggingPostId) return;

    setEditingId(post.id);
    setEditingData({
      title: post.title || "",
      featured_image: post.featured_image || "",
      author: post.author || "",
      categories: post.categories || "",
      content: post.content || "",
      meta_title: post.meta_title || "",
      meta_description: post.meta_description || "",
      meta_keywords: post.meta_keywords || "",
    });
    setActionError("");
    setEditActiveTab(0);
    setArticleDialogOpen(true);
  };

  const closeArticleDialog = () => {
    setArticleDialogOpen(false);
    setEditingId(null);
    setEditActiveTab(0);
    setEditingData({
      title: "",
      featured_image: "",
      author: "",
      categories: "",
      content: "",
      meta_title: "",
      meta_description: "",
      meta_keywords: "",
    });
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;

    if (!editingData.title.trim() || !editingData.content.trim()) {
      setActionError("Title and Content are required.");
      setEditActiveTab(0);
      return;
    }

    const authToken = getToken();
    if (!authToken || !canEdit) {
      setActionError(
        "You do not have permission to edit posts (Recruiter / Administrator only)."
      );
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/companies/${companyId}/posts/${editingId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            title: editingData.title.trim(),
            featured_image: editingData.featured_image.trim() || null,
            author: editingData.author.trim() || null,
            categories: editingData.categories.trim() || null,
            content: editingData.content.trim(), // ✅ may contain HTML
            meta_title: editingData.meta_title.trim() || null,
            meta_description: editingData.meta_description.trim() || null,
            meta_keywords: editingData.meta_keywords.trim() || null,
          }),
        }
      );

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.error || "Failed to update post");
      }

      const updated = await res.json();
      setPosts((prev) => prev.map((p) => (p.id === editingId ? updated : p)));
      closeArticleDialog();
    } catch (err) {
      console.error("Error updating post:", err);
      setActionError(err.message || "Failed to update post");
    } finally {
      setSubmitting(false);
    }
  };

  // ======================
  //   DELETE
  // ======================
  const handleDelete = async (postId) => {
    const authToken = getToken();
    if (!authToken || !canEdit) {
      setActionError(
        "You do not have permission to delete posts (Recruiter / Administrator only)."
      );
      return;
    }

    setSubmitting(true);
    setActionError("");
    try {
      const res = await fetch(
        `${API_BASE}/api/companies/${companyId}/posts/${postId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.error || "Failed to delete post");
      }

      setPosts((prev) => prev.filter((p) => p.id !== postId));
      if (editingId === postId) closeArticleDialog();
    } catch (err) {
      console.error("Error deleting post:", err);
      setActionError(err.message || "Failed to delete post");
    } finally {
      setSubmitting(false);
    }
  };

  // ======================
  //   SEARCH + SORT (client-side)
  // ======================
  const filteredAndSortedPosts = useMemo(() => {
    const q = String(searchQuery || "").trim().toLowerCase();
    let arr = Array.isArray(posts) ? [...posts] : [];

    if (q) {
      arr = arr.filter((p) => {
        const title = String(p?.title || "").toLowerCase();
        const content = String(p?.content || "").toLowerCase();
        return title.includes(q) || content.includes(q);
      });
    }

    const getTime = (p) => {
      return toTimeValue(p?.created_at || p?.updated_at);
    };
    const getTitle = (p) => String(p?.title || "").toLowerCase();

    switch (sortMode) {
      case "date_desc":
        return arr.sort((a, b) => getTime(b) - getTime(a));
      case "date_asc":
        return arr.sort((a, b) => getTime(a) - getTime(b));
      case "title_asc":
        return arr.sort((a, b) => getTitle(a).localeCompare(getTitle(b)));
      case "title_desc":
        return arr.sort((a, b) => getTitle(b).localeCompare(getTitle(a)));
      case "manual":
      default:
        return arr;
    }
  }, [posts, sortMode, searchQuery]);

  // ======================
  //   PAGINATION (after filter/sort)
  // ======================
  const totalPages = Math.max(
    1,
    Math.ceil(filteredAndSortedPosts.length / POSTS_PER_PAGE)
  );

  useEffect(() => {
    setPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  const paginatedPosts = useMemo(() => {
    const startIndex = (page - 1) * POSTS_PER_PAGE;
    return filteredAndSortedPosts.slice(
      startIndex,
      startIndex + POSTS_PER_PAGE
    );
  }, [filteredAndSortedPosts, page]);

  const handlePageChange = (event, value) => setPage(value);

  // ======================
  //   DRAG & DROP REORDER
  // ======================
  const tryPersistOrder = async (nextPosts) => {
    const authToken = getToken();
    if (!authToken || !canEdit) return;

    try {
      const orderedIds = nextPosts.map((p) => p.id);

      const res = await fetch(
        `${API_BASE}/api/companies/${companyId}/posts/reorder`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({ orderedIds }),
        }
      );

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.error || "Failed to save order");
      }
    } catch (e) {
      console.error("Persist order error:", e);
      setActionError(e.message || "Failed to save order");
    }
  };

  const handleDropReorder = async (toAbsIndex) => {
    const fromAbsIndex = dragFromAbsIndexRef.current;
    dragFromAbsIndexRef.current = null;

    setDraggingPostId(null);
    setDragOverPostId(null);

    if (fromAbsIndex == null) return;
    if (toAbsIndex == null) return;
    if (fromAbsIndex === toAbsIndex) return;

    const hasSearch = String(searchQuery || "").trim().length > 0;
    if (sortMode !== "manual" || hasSearch) {
      setActionError(
        hasSearch
          ? "Clear search to reorder posts."
          : "Switch Sort to “Manual (Drag order)” to reorder posts."
      );
      return;
    }

    setPosts((prev) => {
      const next = moveItem(prev, fromAbsIndex, toAbsIndex);
      tryPersistOrder(next);
      return next;
    });
  };

  // ======================
  //   RENDER
  // ======================
  return (
    <>
      <Card sx={{ mt: 3, borderRadius: 0, boxShadow: "none" }}>
        <TabSectionHeader
          title="Articles"
          dividerColor={TAB_LIGHT_BLUE}
          actionNode={
            <Stack direction="row" spacing={1} alignItems="center">
              {/* ✅ Search bar */}
              <TextField
                size="small"
                placeholder="Search title or content…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ width: { xs: 180, sm: 260, md: 320 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              {/* ✅ Sort dropdown */}
              <FormControl size="small" sx={{ minWidth: 190 }}>
                <InputLabel id="posts-sort-label">Sort</InputLabel>
                <Select
                  labelId="posts-sort-label"
                  value={sortMode}
                  label="Sort"
                  onChange={(e) => setSortMode(e.target.value)}
                >
                  <MenuItem value="manual">Manual (Drag order)</MenuItem>
                  <MenuItem value="date_desc">Date: Newest first</MenuItem>
                  <MenuItem value="date_asc">Date: Oldest first</MenuItem>
                  <MenuItem value="title_asc">Title: A → Z</MenuItem>
                  <MenuItem value="title_desc">Title: Z → A</MenuItem>
                </Select>
              </FormControl>

              {canEdit && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={() => {
                    setActionError("");
                    setAiPrompt("");
                    setNewPostDialogOpen(true);
                  }}
                >
                  Add New
                </Button>
              )}
            </Stack>
          }
        />
        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          {loading && (
            <Typography variant="body2" color="text.secondary">
              Loading posts…
            </Typography>
          )}

          {loadError && (
            <Typography variant="body2" color="error" mb={2}>
              {loadError}
            </Typography>
          )}

          {actionError && (
            <Typography variant="body2" color="error" mb={2}>
              {actionError}
            </Typography>
          )}

          {!loading && filteredAndSortedPosts.length === 0 && !loadError && (
            <Typography variant="body2" color="text.secondary">
              {posts.length === 0
                ? "No posts yet for this company."
                : "No posts match your search."}
            </Typography>
          )}

          {filteredAndSortedPosts.length > 0 && (
            <>
              <Box
                sx={{
                  mt: 2,
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(3, minmax(0, 1fr))",
                  },
                }}
              >
                {paginatedPosts.map((post, idxOnPage) => {
                  const categories = parseCategories(post.categories);
                  const absIndex = (page - 1) * POSTS_PER_PAGE + idxOnPage;

                  const isDragging = draggingPostId === post.id;
                  const isDragOver = dragOverPostId === post.id;

                  const dragEnabled =
                    canEdit &&
                    sortMode === "manual" &&
                    String(searchQuery || "").trim().length === 0;

                  return (
                    <Box
                      key={post.id}
                      draggable={dragEnabled}
                      onDragStart={(e) => {
                        if (!dragEnabled) return;
                        setActionError("");
                        dragFromAbsIndexRef.current = absIndex;
                        setDraggingPostId(post.id);

                        try {
                          e.dataTransfer.setData("text/plain", String(post.id));
                        } catch {}
                        e.dataTransfer.effectAllowed = "move";
                      }}
                      onDragEnd={() => {
                        dragFromAbsIndexRef.current = null;
                        setDraggingPostId(null);
                        setDragOverPostId(null);
                      }}
                      onDragOver={(e) => {
                        if (!dragEnabled) return;
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                        if (dragOverPostId !== post.id)
                          setDragOverPostId(post.id);
                      }}
                      onDragLeave={() => {
                        if (dragOverPostId === post.id) setDragOverPostId(null);
                      }}
                      onDrop={(e) => {
                        if (!dragEnabled) return;
                        e.preventDefault();
                        handleDropReorder(absIndex);
                      }}
                      sx={{
                        borderRadius: 2,
                        border: "1px solid #e5e7eb",
                        p: 2,
                        backgroundColor: "#fff",
                        cursor: dragEnabled ? "grab" : "default",
                        opacity: isDragging ? 0.55 : 1,
                        transform: isDragOver ? "scale(1.01)" : "none",
                        boxShadow: isDragOver
                          ? "0 0 0 2px rgba(124,58,237,0.25)"
                          : "none",
                        transition:
                          "transform 120ms ease, box-shadow 120ms ease, opacity 120ms ease",
                        userSelect: "none",
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="flex-start"
                        justifyContent="space-between"
                        flexDirection={"column"}
                        spacing={1.5}
                      >
                        <Box sx={{ flex: 1, width: "100%" }}>
                          {canEdit && (
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              sx={{ mb: 1 }}
                            >
                              <Tooltip
                                title={
                                  dragEnabled
                                    ? "Drag to reorder"
                                    : String(searchQuery || "").trim()
                                    ? "Clear search to reorder"
                                    : "Switch Sort to “Manual (Drag order)” to reorder"
                                }
                              >
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={0.5}
                                  sx={{
                                    color: "text.secondary",
                                    cursor: dragEnabled ? "grab" : "not-allowed",
                                    opacity: dragEnabled ? 1 : 0.6,
                                  }}
                                  onMouseDown={(e) => e.stopPropagation()}
                                >
                                  <DragIndicatorIcon fontSize="small" />
                                  <Typography variant="caption">
                                    Reorder
                                  </Typography>
                                </Stack>
                              </Tooltip>

                              <Stack
                                spacing={0.5}
                                alignItems="flex-end"
                                flexDirection={"row"}
                              >
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    startEdit(post);
                                  }}
                                >
                                  <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDuplicate(post);
                                  }}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(post.id);
                                  }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            </Stack>
                          )}

                          {post.featured_image && (
                            <Box
                              component="img"
                              src={post.featured_image}
                              alt={post.title}
                              onError={(e) => {
                                // hide if broken URL
                                e.currentTarget.style.display = "none";
                              }}
                              sx={{
                                width: "100%",
                                height: 140,
                                borderRadius: 1.5,
                                objectFit: "cover",
                                mb: 1,
                                border: "1px solid #e5e7eb",
                                pointerEvents: "none",
                              }}
                              draggable={false}
                            />
                          )}

                          <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            sx={{ cursor: canEdit ? "pointer" : "default" }}
                            onClick={() => canEdit && startEdit(post)}
                          >
                            {post.title}
                          </Typography>

                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mt: 0.5 }}
                          >
                            {post.author ? `By ${post.author}` : "Author unknown"}
                            {post.created_at && (
                              <>
                                {" · Created "}
                                {formatDateTime(post.created_at)}
                              </>
                            )}
                            {post.updated_at &&
                              post.updated_at !== post.created_at && (
                                <>
                                  {" · Updated "}
                                  {formatDateTime(post.updated_at)}
                                </>
                              )}
                          </Typography>

                          {categories.length > 0 && (
                            <Stack
                              direction="row"
                              spacing={0.5}
                              flexWrap="wrap"
                              mt={0.75}
                            >
                              {categories.map((cat) => (
                                <Chip
                                  key={cat}
                                  label={cat}
                                  size="small"
                                  sx={{
                                    height: 22,
                                    fontSize: 11,
                                    borderRadius: 999,
                                  }}
                                />
                              ))}
                            </Stack>
                          )}

                          {/* ✅ HTML rendering (SANITIZED) */}
                          <Box
                            sx={{
                              mt: 1,
                              fontSize: 14,
                              lineHeight: 1.6,
                              display: "-webkit-box",
                              WebkitLineClamp: 4,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              "& p": { margin: "0 0 8px 0" },
                              "& ul": {
                                paddingLeft: "18px",
                                margin: "0 0 8px 0",
                              },
                              "& li": { marginBottom: "4px" },
                              "& strong": { fontWeight: 600 },
                              "& a": {
                                color: "#2563eb",
                                textDecoration: "underline",
                              },
                            }}
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(
                                String(post.content || "")
                              ),
                            }}
                          />
                        </Box>
                      </Stack>

                      <Divider sx={{ mt: 1.5, mb: 0.5, opacity: 0.4 }} />
                    </Box>
                  );
                })}
              </Box>

              {totalPages > 1 && (
                <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="small"
                    sx={{
                      "& .MuiPagination-ul": { justifyContent: "center" },
                      "& .MuiPaginationItem-root": {
                        width: 32,
                        minWidth: 32,
                        height: 32,
                        margin: "0 3px",
                      },
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ✅ New Post Dialog (PROMPT ONLY) */}
      <Dialog
        open={newPostDialogOpen}
        onClose={() => {
          if (submitting) return;
          setNewPostDialogOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle data-popup-header
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 0,
          }}
        >
          <Typography data-popup-title variant="h6" fontWeight={600}>
            Create Post(s) with OpenAI Prompt
          </Typography>
          <IconButton data-popup-close
            size="small"
            onClick={() => {
              if (submitting) return;
              setNewPostDialogOpen(false);
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 2 }}>
          <Box
            sx={{
              borderRadius: 2,
              border: "1px solid #e5e7eb",
              p: 2,
              background: "#f9fafb",
            }}
          >
            <Stack spacing={1.25}>
              <Typography variant="body2" color="text.secondary">
                Write a prompt to generate articles. The API should return a JSON
                array with:{" "}
                <strong>
                  featured_image, publish_date, title, author, content
                </strong>
                .
              </Typography>

              <TextField
                label="OpenAI Prompt"
                placeholder={`Example:
Find 3 recent articles about {company} in the staffing/recruiting space.
Return ONLY valid JSON array. Each item must include:
featured_image, publish_date, title, author, content.
Content should be 600-900 words, HTML allowed.`}
                multiline
                minRows={8}
                fullWidth
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
              />

              <Typography variant="caption" color="text.secondary">
                Endpoint used: <code>/api/companies/:companyId/posts/ai</code>{" "}
                then creates posts via{" "}
                <code>/api/companies/:companyId/posts</code>.
              </Typography>
            </Stack>
          </Box>
        </DialogContent>

        <DialogActions data-popup-footer sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setNewPostDialogOpen(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateFromPrompt}
            disabled={submitting}
          >
            {submitting ? "Generating…" : "Generate & Publish"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* EDIT DIALOG (UNCHANGED) */}
      <Dialog
        open={articleDialogOpen}
        onClose={closeArticleDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle data-popup-header
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 0,
          }}
        >
          <Typography data-popup-title variant="h6" fontWeight={600}>
            Edit Post
          </Typography>
          <IconButton data-popup-close size="small" onClick={closeArticleDialog}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 1 }}>
          <Tabs
            value={editActiveTab}
            onChange={(e, val) => setEditActiveTab(val)}
            sx={{
              mb: 2,
              borderBottom: "1px solid #e5e7eb",
              "& .MuiTab-root": { textTransform: "none", fontSize: 14 },
            }}
          >
            <Tab label="Edit" />
            <Tab label="SEO" />
          </Tabs>

          {editActiveTab === 0 && (
            <Box
              sx={{
                borderRadius: 2,
                border: "1px solid #e5e7eb",
                p: 2,
                background: "#f9fafb",
              }}
            >
              <Stack spacing={1.5}>
                <TextField
                  label="Title"
                  size="small"
                  fullWidth
                  value={editingData.title}
                  onChange={(e) =>
                    setEditingData((prev) => ({
                      ...prev,
                      title: e.target.value,
                    }))
                  }
                />

                <Stack direction="row" spacing={1.5} alignItems="center">
                  <TextField
                    label="Featured Image URL"
                    size="small"
                    fullWidth
                    value={editingData.featured_image}
                    onChange={(e) =>
                      setEditingData((prev) => ({
                        ...prev,
                        featured_image: e.target.value,
                      }))
                    }
                  />
                  <Box>
                    <input
                      type="file"
                      accept="image/*"
                      ref={editImageInputRef}
                      style={{ display: "none" }}
                      onChange={handleEditImageFileChange}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => editImageInputRef.current?.click()}
                      disabled={uploadingEditImage || submitting}
                    >
                      {uploadingEditImage ? "Uploading…" : "Upload"}
                    </Button>
                  </Box>
                </Stack>

                {editingData.featured_image && (
                  <Box
                    component="img"
                    src={editingData.featured_image}
                    alt="Featured preview"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                    sx={{
                      mt: 1,
                      width: 160,
                      height: 96,
                      objectFit: "cover",
                      borderRadius: 1.5,
                      border: "1px solid #e5e7eb",
                    }}
                    draggable={false}
                  />
                )}

                <TextField
                  label="Categories"
                  helperText="Comma separated"
                  size="small"
                  fullWidth
                  value={editingData.categories}
                  onChange={(e) =>
                    setEditingData((prev) => ({
                      ...prev,
                      categories: e.target.value,
                    }))
                  }
                />

                <TextField
                  label="Content (HTML allowed)"
                  multiline
                  minRows={4}
                  fullWidth
                  value={editingData.content}
                  onChange={(e) =>
                    setEditingData((prev) => ({
                      ...prev,
                      content: e.target.value,
                    }))
                  }
                />

                {/* ✅ Optional: small sanitized preview inside edit dialog */}
                {String(editingData.content || "").trim() && (
                  <Box
                    sx={{
                      border: "1px dashed #e5e7eb",
                      borderRadius: 2,
                      p: 1.5,
                      background: "#fff",
                      "& p": { margin: "0 0 8px 0" },
                      "& ul": { paddingLeft: "18px", margin: 0 },
                      "& li": { marginBottom: "4px" },
                      "& strong": { fontWeight: 600 },
                      "& a": {
                        color: "#2563eb",
                        textDecoration: "underline",
                      },
                    }}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(String(editingData.content)),
                    }}
                  />
                )}
              </Stack>
            </Box>
          )}

          {editActiveTab === 1 && (
            <Box
              sx={{
                borderRadius: 2,
                border: "1px solid #e5e7eb",
                p: 2,
                background: "#f9fafb",
              }}
            >
              <Stack spacing={1.5}>
                <TextField
                  label="Meta Title"
                  size="small"
                  fullWidth
                  value={editingData.meta_title}
                  onChange={(e) =>
                    setEditingData((prev) => ({
                      ...prev,
                      meta_title: e.target.value,
                    }))
                  }
                />
                <TextField
                  label="Meta Description"
                  size="small"
                  fullWidth
                  multiline
                  minRows={3}
                  value={editingData.meta_description}
                  onChange={(e) =>
                    setEditingData((prev) => ({
                      ...prev,
                      meta_description: e.target.value,
                    }))
                  }
                />
                <TextField
                  label="Meta Keywords"
                  size="small"
                  fullWidth
                  value={editingData.meta_keywords}
                  onChange={(e) =>
                    setEditingData((prev) => ({
                      ...prev,
                      meta_keywords: e.target.value,
                    }))
                  }
                />
              </Stack>
            </Box>
          )}
        </DialogContent>

        <DialogActions data-popup-footer sx={{ px: 3, py: 2 }}>
          <Button onClick={closeArticleDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveOutlinedIcon />}
            onClick={handleSaveEdit}
            disabled={submitting}
          >
            {submitting ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
